import * as React from "react";
import { connect } from "react-redux";
import { QueryTable, QueryTableColumn } from "../../misc/components/queryTable/QueryTable";
import { Query, User, QueryCondition, Permission } from "../../misc/api/apiTypes";
import MultiSelectModal from '../../misc/components/queryTable/MultiSelectModal';
import { HamburgerMenu } from "../../misc/components/HamburgerMenu";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { Authorization, GlobalAuthorization } from "../../misc/components/Authorization";
import Condition from "../../misc/components/Condition";
import { MainState } from "../../Core/reducers";
import BooleanSelectModal from "../../misc/components/queryTable/BooleanSelectModal";
import { QueryTableState, ReceiveQueryAction, PageAction, SortAction, FilterAction } from "../../misc/components/queryTable/Hooks/UseQueryTable";
import { useSecurityContext } from "../../Core/SecurityContext";
import { Link } from "react-router-dom";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

declare var rootUrl: string;

interface UserListTableState {
    showEmailFilter: boolean;
    showUsernameFilter: boolean;
    showIsDisabledFilter: boolean;
    emailFilterQuery: Query<User>;
}

const initialUserTableState: UserListTableState = {
    showEmailFilter: false,
    showUsernameFilter: false,
    showIsDisabledFilter: false,
    emailFilterQuery: {
        Filters: [],
        Members: ['Id', 'Email', 'Username'],
    },
}

export interface UserListTableProps {
    queryTableReducer: [QueryTableState, React.Dispatch<any>];
    currentStudyId: string;
}

export const UserListTable = ({ queryTableReducer, currentStudyId }: UserListTableProps) => {
    const { currentUserPermissions } = useSecurityContext();
    const [queryTableState, dispatch] = queryTableReducer;
    const legacyClient = useWebPortalLegacyClient();
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        let mounted = true;
        const fetchUsers = async () => {
            if (mounted) {
                setIsLoading(true);
                const userQuery = await legacyClient.getUsers(queryTableState.query);
                dispatch(ReceiveQueryAction.create(userQuery));
                setIsLoading(false);
            }
        }

        fetchUsers();

        return () => { mounted = false };
    }, [queryTableState.query.Page.Current, queryTableState.query.Order.Member, queryTableState.query.Order.Direction, queryTableState.query.Filters]);

    // Non-generic
    const userReducer = (state: UserListTableState, action): UserListTableState => {
        switch (action.type) {
            case 'SetEmailsFilterVisiblity':
                return {
                    ...state,
                    showEmailFilter: action.isVisible,
                };
            case 'SetUsernameFilterVisiblity':
                return {
                    ...state,
                    showUsernameFilter: action.isVisible,
                };
            case 'SetIsDisabledFilterVisiblity':
                return {
                    ...state,
                    showIsDisabledFilter: action.isVisible,
                };
            case 'ReceiveEmails':
                return {
                    ...state,
                    emailFilterQuery: action.query,
                };
            default:
                throw new Error("Unexpected action");
        }
    }

    const [currentUserTableState, dispatchUser] = React.useReducer(userReducer, initialUserTableState);

    React.useEffect(() => {
        const fetchEmails = async () => {
            const emailsQuery = await legacyClient.getUsers(currentUserTableState.emailFilterQuery);

            dispatchUser({ type: 'ReceiveEmails', query: emailsQuery });
        }

        fetchEmails();
    }, [])

    const handlePage = (page: number) => {
        !isLoading && dispatch(PageAction.create(page));
    }

    const handleSort = (member: string) => {
        dispatch(SortAction.create(member));
    }

    const handleFilter = (member: string) => {
        switch (member) {
            case 'Email':
                dispatchUser({ type: 'SetEmailsFilterVisiblity', isVisible: true });
                break;
            case 'Username':
                dispatchUser({ type: 'SetUsernameFilterVisiblity', isVisible: true });
                break;
            case 'IsDisabled':
                dispatchUser({ type: 'SetIsDisabledFilterVisiblity', isVisible: true });
                break;
        }

    }

    const handleMultiSelectFilterChange = (member: string, values: string[]): void => {
        dispatch(FilterAction.create(member, QueryCondition.Equal, values));
    };

    const handleEmailFilterChange = (values): void =>
        handleMultiSelectFilterChange("Email", values.map(v => v.Email));

    const handleUsernameFilterChange = (values): void =>
        handleMultiSelectFilterChange("Username", values.map(v => v.Username));

    const handleIsDisabledFilterChange = (values: string[]): void =>
        handleMultiSelectFilterChange("IsDisabled", values);

    const handleFilterClose = () => {
        dispatchUser({ type: 'SetEmailsFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetUsernameFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetIsDisabledFilterVisiblity', isVisible: false });
    }

    const getFilterValues = (member: string, condition: QueryCondition): any =>
        queryTableState.query &&
        queryTableState.query.Filters &&
        queryTableState.query.Filters.find(item => item.Member === member && item.Condition === condition) &&
        queryTableState.query.Filters.find(item => item.Member === member && item.Condition === condition).Values;

    const renderRoles = (row: User) => {
        return row.Roles.map(r => (`(${r.Study ? r.Study.Name : 'All Studies'}) ${r.Name}`)).join(', ');
    }

    const renderHamburgerMenu = (row: User) => {
        return (
            <HamburgerMenu>
                <GlobalAuthorization permissionId={PermissionEnum.EditUsers} currentPermissions={currentUserPermissions}>
                    <Condition test={() => !row.IsServiceAccount}>
                        <li>
                            <Link to={`/users/edit/${row.Id}`}>Edit</Link>
                        </li>
                    </Condition>
                </GlobalAuthorization>
            </HamburgerMenu>
        );
    }

    return (
        <>
            <QueryTable id="usersTable" query={queryTableState.query} rowUniqueIdentifier={row => row.Id} onFilter={handleFilter} onSort={(m) => handleSort(m)} onPage={(p) => handlePage(p)}>
                <QueryTableColumn member="Email" header="Email" map={row => row.Email} sortable={true} filterable={true} />
                <QueryTableColumn member="Username" header="Username" map={row => row.Username} sortable={true} filterable={true} />
                <QueryTableColumn member="Roles.Name" header="Roles" map={row => renderRoles(row)} sortable={false} filterable={false} />
                <QueryTableColumn member="" header="" map={row => renderHamburgerMenu(row)} sortable={false} filterable={false} />
            </QueryTable>
            <MultiSelectModal
                header="Email Filter"
                show={currentUserTableState.showEmailFilter}
                options={currentUserTableState.emailFilterQuery.Data}
                valueKey={"Email"}
                labelKey={"Email"}
                values={getFilterValues("Email", QueryCondition.Equal)}
                onChange={handleEmailFilterChange}
                onClose={handleFilterClose}
            />
            <MultiSelectModal
                header="Username Filter"
                show={currentUserTableState.showUsernameFilter}
                options={currentUserTableState.emailFilterQuery.Data}
                valueKey={"Username"}
                labelKey={"Username"}
                values={getFilterValues("Username", QueryCondition.Equal)}
                onChange={handleUsernameFilterChange}
                onClose={handleFilterClose}
            />
            <BooleanSelectModal
                header="Disabled Filter"
                options={[
                    { id: true, name: 'Disabled' },
                    { id: false, name: 'Enabled' },
                ]}
                show={currentUserTableState.showIsDisabledFilter}
                value={getFilterValues("IsDisabled", QueryCondition.Equal)}
                onChange={handleIsDisabledFilterChange}
                onClose={handleFilterClose}
            />
        </>
    );
}

function mapStateToProps(state: MainState) {
    return {
        currentStudyId: state.security.studyId,
    }
}

export default connect(mapStateToProps)(UserListTable);
